import { BaseThreeScene } from '@modules/three/common/BaseThreeScene';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import { exportStore } from '@export/store/exportStore.ts';
import ExportService from '@export/three/ExportService.ts';
import JawsExport from '@export/three/JawsExport.ts';
import type JawExport from '@export/three/JawExport.ts';
import { createAlignerMaterial } from '@export/three/materials/alignerMaterial.ts';
import { generateAlignerShell } from '@export/aligner/AlignerShellGenerator';
import type {
  AlignerDebugStage,
  AlignerShellRequest,
  AlignerShellResult,
} from '@export/aligner/AlignerShellGenerator';
import type { TrimCurveJaw, TrimCurvePoint } from '@export/store/exportStore.ts';
import type { BandVoxelDebugLayers } from '@export/aligner/alignerBandDebug';
import { BrushVisualizer } from '@modules/three/tools/BrushVisualizer';
import { toolManager } from '@modules/three/tools/ToolManager';
import { TrimThickeningTool, type TrimThickeningHost } from '@export/three/TrimThickeningTool';
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineLoop,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  Points,
  PointsMaterial,
  ShaderMaterial,
  Vector3,
} from 'three';

type AlignerSanitizedParams = {
  shellOffset: number;
  thickness: number;
  voxelSize: number;
  blockoutStrength: number;
  trimRimClamp: boolean;
};

// Trim-point marker shader: per-point screen-space size + color, drawn as round points.
const TRIM_MARKER_VERTEX = /* glsl */ `
  attribute float aSize;
  attribute vec3 aColor;
  varying vec3 vColor;
  void main() {
    vColor = aColor;
    gl_PointSize = aSize;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const TRIM_MARKER_FRAGMENT = /* glsl */ `
  varying vec3 vColor;
  void main() {
    vec2 d = gl_PointCoord - vec2(0.5);
    if (dot(d, d) > 0.25) discard;
    gl_FragColor = vec4(vColor, 1.0);
  }
`;
// Monochrome ramp of the UI primary (#1d4ed8): primary → lighter → lightest, so state reads
// as brightness of one hue. Selected is the brightest (it wins over hover).
const TRIM_MARKER_PRIMARY = 0x1d4ed8;
const TRIM_MARKER_BASE_COLOR = new Color(TRIM_MARKER_PRIMARY); // primary blue
const TRIM_MARKER_HOVER_COLOR = new Color(TRIM_MARKER_PRIMARY).lerp(new Color(0xffffff), 0.4); // lighter (under brush)
const TRIM_MARKER_SELECTED_COLOR = new Color(TRIM_MARKER_PRIMARY).lerp(new Color(0xffffff), 0.7); // lightest (selected)
const TRIM_MARKER_BASE_SIZE = 7; // px
const TRIM_MARKER_HIGHLIGHT_BUMP = 4; // px when hovered/selected
const TRIM_MARKER_SIZE_PER_MM = 9; // px of extra size per mm of thickening (visual "thicker")

export class ExportScene extends BaseThreeScene {
  private jawsExport?: JawsExport;
  private readonly exportService = new ExportService();
  private requestId = 0;
  private readonly alignerGroup = new Group();
  private readonly debugTrimGroup = new Group();
  private readonly bandVoxelDebugGroup = new Group();
  private unsubscribeCappingDebug: (() => void) | null = null;
  private unsubscribeBandVoxelDebug: (() => void) | null = null;
  private unsubscribeTrimThickening: (() => void) | null = null;

  // Trim-curve thickening phase
  private readonly trimThickeningGroup = new Group();
  private activeTrimJaw: TrimCurveJaw = 'lower';
  private trimMarkers: Points | null = null;
  private trimMarkerPoints: Vector3[] = [];
  private trimBrush: BrushVisualizer | null = null;
  private readonly trimTool: TrimThickeningTool;
  private readonly trimHovered = new Set<number>();
  private readonly trimPreview = new Set<number>();
  private readonly lastJawVisible: Record<TrimCurveJaw, boolean> = { upper: true, lower: true };

  constructor(container: HTMLElement) {
    super(container);
    this.root.camera.position.set(0, 0, 150);
    this.root.renderer.setClearColor('#020617');
    this.alignerGroup.name = 'AlignerShells';
    this.root.scene.add(this.alignerGroup);
    this.debugTrimGroup.name = 'TrimCurveDebug';
    this.root.scene.add(this.debugTrimGroup);
    this.bandVoxelDebugGroup.name = 'BandVoxelDebug';
    this.root.scene.add(this.bandVoxelDebugGroup);
    this.trimThickeningGroup.name = 'TrimThickening';
    this.root.scene.add(this.trimThickeningGroup);

    this.trimTool = new TrimThickeningTool(this.createTrimThickeningHost());
    toolManager.register(this.trimTool);
    this.unsubscribeTrimThickening = exportStore.subscribe((state, prev) => {
      if (state.trimThickeningActive !== prev.trimThickeningActive) {
        if (state.trimThickeningActive) this.enterTrimThickening();
        else this.exitTrimThickening();
      } else if (
        state.trimThickeningActive &&
        (state.trimSelectedPoints !== prev.trimSelectedPoints ||
          state.trimThickness !== prev.trimThickness)
      ) {
        this.refreshTrimMarkers();
      }
    });

    this.unsubscribeCappingDebug = exportStore.subscribe((state, prev) => {
      if (state.cappingDebugVisible !== prev.cappingDebugVisible) {
        this.applyCappingDebugVisibility(state.cappingDebugVisible);
      }
    });

    this.unsubscribeBandVoxelDebug = exportStore.subscribe((state, prev) => {
      if (state.bandVoxelDebugVisible !== prev.bandVoxelDebugVisible) {
        this.bandVoxelDebugGroup.visible = state.bandVoxelDebugVisible;
      }
    });
    this.bandVoxelDebugGroup.visible = exportStore.getState().bandVoxelDebugVisible;
  }

  private applyCappingDebugVisibility(visible: boolean) {
    if (!this.jawsExport) return;
    this.jawsExport.upperJaw?.setCappingDebugVisible(visible);
    this.jawsExport.lowerJaw?.setCappingDebugVisible(visible);
  }

  /**
   * The scene-level trim overlays don't inherit jaw visibility: the debug curve + thickening
   * markers live under scene-level groups, and each jaw's InteractiveSpline control points add
   * THEMSELVES to the scene root. Keep all of them in sync with their jaw's visibility so
   * hiding a jaw also hides its trim curve, handles and markers.
   */
  protected override beforeRender(): void {
    if (!this.jawsExport) return;
    const activeJaw =
      this.activeTrimJaw === 'upper' ? this.jawsExport.upperJaw : this.jawsExport.lowerJaw;
    const activeVisible = activeJaw ? activeJaw.visible : true;
    this.debugTrimGroup.visible = activeVisible;
    this.trimThickeningGroup.visible = activeVisible;
    this.syncJawCurveVisibility('upper', this.jawsExport.upperJaw);
    this.syncJawCurveVisibility('lower', this.jawsExport.lowerJaw);
  }

  /** Toggle a jaw's spline control points (scene-attached) on jaw-visibility change only. */
  private syncJawCurveVisibility(key: TrimCurveJaw, jaw?: JawExport) {
    if (!jaw || this.lastJawVisible[key] === jaw.visible) return;
    this.lastJawVisible[key] = jaw.visible;
    jaw.trimCurve?.setVisible(jaw.visible);
  }

  public async init(segmentation: JawsSegmentationResponse) {
    await this.exportService.prepare(segmentation);
    this.startRendering();
  }

  private replaceJaws(next: JawsExport | null) {
    if (this.jawsExport) {
      this.root.scene.remove(this.jawsExport);
      this.exportService.disposeJaws(this.jawsExport);
      this.jawsExport = undefined;
    }
    if (next) {
      this.root.scene.add(next);
      this.jawsExport = next;
      this.applyCappingDebugVisibility(exportStore.getState().cappingDebugVisible);
    }
    exportStore.getState().setJaws(next);
  }

  public async displayCorrection(correction: JawsCorrectionResponse | null) {
    const currentRequest = ++this.requestId;
    const jaws = await this.exportService.createJawsExport(correction);
    if (currentRequest !== this.requestId) {
      this.exportService.disposeJaws(jaws);
      return;
    }
    this.replaceJaws(jaws);
    if (jaws) {
      this.autoGenerateTrimCurve('lower');
    } else {
      this.clearDebugTrim();
      exportStore.getState().setTrimCurveState('idle');
    }
  }

  public async generateAlignerShellFromUI(options: AlignerShellRequest) {
    if (!this.jawsExport) {
      console.warn('[ExportScene] No jaws loaded, cannot generate aligner shell.');
      exportStore.getState().setAlignerStatus({ state: 'error', message: 'Load a correction before generating an aligner.' });
      return;
    }

    const trimState = exportStore.getState().trimCurveState;
    if (trimState !== 'ready') {
      exportStore.getState().setAlignerStatus({
        state: 'error',
        message: 'Lower jaw trim curve is not ready yet. Wait for the export scene to finish loading.',
      });
      return;
    }

    exportStore.getState().setAlignerStatus({ state: 'pending' });

    const trimCurves = exportStore.getState().trimCurves;
    const sanitized: AlignerSanitizedParams = {
      shellOffset: this.ensurePositive(options.shellOffset, 0.25, 0.05),
      thickness: this.ensurePositive(options.thickness, 0.8, 0.1),
      voxelSize: this.ensurePositive(options.voxelSize, 0.3, 0.05),
      blockoutStrength:
        Number.isFinite(options.blockoutStrength) && options.blockoutStrength >= 0
          ? Math.min(1, options.blockoutStrength)
          : 0.7,
      trimRimClamp: options.trimRimClamp ?? true,
    };

    const targets: Array<{ jaw: JawExport; label: TrimCurveJaw }> = [];
    if (options.jaw === 'upper' || options.jaw === 'both') {
      const jaw = this.jawsExport?.upperJaw;
      if (jaw) targets.push({ jaw, label: 'upper' });
    }
    if (options.jaw === 'lower' || options.jaw === 'both') {
      const jaw = this.jawsExport?.lowerJaw;
      if (jaw) targets.push({ jaw, label: 'lower' });
    }
    if (targets.length === 0) {
      console.warn('[ExportScene] Invalid jaw selection for aligner generation.');
      exportStore.getState().setAlignerStatus({ state: 'error', message: 'Select a jaw to generate the aligner for.' });
      return;
    }

    this.root.scene.updateMatrixWorld(true);
    this.clearAligners();

    // Yield once so React can paint the "Generating…" overlay before the heavy (partly
    // synchronous) generation work begins on the main thread.
    await new Promise((resolve) => setTimeout(resolve, 0));

    let successMeta: { label: string; metadata: AlignerShellResult['metadata'] } | null = null;
    let lastError: string | null = null;

    for (const { jaw, label } of targets) {
      if (!jaw) return;

      const oppositeJaw = label === 'upper' ? this.jawsExport?.lowerJaw : this.jawsExport?.upperJaw;

      if (oppositeJaw) {
        oppositeJaw.visible = false;
        oppositeJaw.teeth.forEach((t) => {
          t.visible = false;
        });
      }
      jaw.visible = true;
      jaw.teeth.forEach((tooth) => {
        tooth.visible = true;
      });

      try {
        // No separate "confirm" step: read the trim curve the user is currently editing
        // straight off the live spline, falling back to the last stored copy.
        const livePoints = this.resolveTrimCurvePoints(jaw);
        const trimCurvePoints =
          livePoints && livePoints.length >= 3
            ? livePoints
            : (trimCurves[label] ?? []).map((p) => new Vector3(p.x, p.y, p.z));
        if (trimCurvePoints.length < 3) {
          exportStore.getState().setAlignerStatus({
            state: 'error',
            message: `No ${label} trimming curve available yet — wait for the scene to finish loading.`,
          });
          if (oppositeJaw) {
            oppositeJaw.visible = true;
            oppositeJaw.teeth.forEach((t) => {
              t.visible = true;
            });
          }
          return;
        }
        // Persist what we're generating from so the markers / thickening stay in sync.
        exportStore
          .getState()
          .setTrimCurve(label, trimCurvePoints.map((p) => ({ x: p.x, y: p.y, z: p.z })));
        this.activeTrimJaw = label;

        const insertionDir = label === 'upper' ? new Vector3(0, 0, -1) : new Vector3(0, 0, 1);
        const teethMeshes = jaw.getCappedTeethMeshes();
        const uncappedTeethMeshes = jaw.getUncappedTeethMeshes();
        const gumMesh = jaw as unknown as Mesh;

        console.info('[ExportScene] aligner generate', {
          jaw: label,
          teethCount: teethMeshes.length,
          debugStage: options.debugStage ?? 'final',
          insertionDir: insertionDir.toArray(),
          trimCurvePoints: trimCurvePoints.length,
          params: sanitized,
        });

        const showBandVoxelDebug = exportStore.getState().bandVoxelDebugVisible;

        const result = await this.generateShell({
          teethMeshes,
          uncappedTeethMeshes,
          gumMesh,
          insertionDir,
          trimCurve: trimCurvePoints,
          trimThickness: exportStore.getState().trimThickness[label],
          sanitized,
          debugStage: options.debugStage,
          showBandVoxelDebug,
        });

        const resultGeometry = result.surfaceGeometry;
        const metadata = result.metadata;

        console.log('[ExportScene] aligner stats:', metadata);

        const mesh = new Mesh(resultGeometry, createAlignerMaterial());
        mesh.name = `AlignerShell-${label}`;
        mesh.renderOrder = 100;
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        this.alignerGroup.add(mesh);

        if (result.voxelDebug) {
          this.showBandVoxelDebug(result.voxelDebug, sanitized.voxelSize);
        }
        if (result.debugCutGeometry) {
          this.showTrimCutDebug(result.debugCutGeometry);
        }
        console.info(`[ExportScene] Generated ${label} aligner shell`, metadata);
        successMeta = { label, metadata };
      } catch (error) {
        console.error(`[ExportScene] Failed to generate ${label} aligner shell`, error);
        lastError = error instanceof Error ? error.message : 'Unknown error during aligner generation.';
      }
      
      // Keep jaws hidden after generation - only show the aligner shell
      // User can toggle visibility in UI if needed
    }

    if (successMeta) {
      exportStore.getState().setAlignerStatus({
        state: 'success',
        jaw: options.jaw,
        metadata: successMeta.metadata,
        generatedAt: Date.now(),
      });
    } else {
      exportStore.getState().setAlignerStatus({
        state: 'error',
        message: lastError ?? 'Could not generate aligner geometry with the current parameters.',
      });
    }
  }


  private async generateShell({
    teethMeshes,
    uncappedTeethMeshes,
    gumMesh,
    insertionDir,
    trimCurve,
    trimThickness,
    sanitized,
    debugStage,
    showBandVoxelDebug,
  }: {
    teethMeshes: Mesh[];
    uncappedTeethMeshes: Mesh[];
    gumMesh: Mesh | null;
    insertionDir: Vector3;
    trimCurve: Vector3[];
    trimThickness?: number[];
    sanitized: AlignerSanitizedParams;
    debugStage?: AlignerDebugStage;
    showBandVoxelDebug?: boolean;
  }): Promise<AlignerShellResult> {
    return generateAlignerShell(teethMeshes, gumMesh, {
      insertionDir,
      voxelSize: sanitized.voxelSize,
      shellOffset: sanitized.shellOffset,
      thickness: sanitized.thickness,
      gapFillRadius: Math.max(sanitized.voxelSize * 3, 0.9),
      blockoutStrength: sanitized.blockoutStrength,
      // Snap the extracted rim onto the trim curve (marching-cubes chamfer + smoothing sag
      // otherwise leave it hanging up to ~a voxel gingival of the curve).
      trimRimClamp: sanitized.trimRimClamp,
      gumMesh,
      // Real tooth surfaces (no gum cap) — the shell band offsets from these so it never
      // follows the synthetic cap; the capped meshes only seal the volume.
      uncappedTeethMeshes,
      // Cut at the gingival margin (no downward cover). Teeth are capped at the margin, so a
      // downward bias would keep the cap ring as a closed floor.
      trimMargin: 0,
      debugStage: debugStage ?? 'final',
      trimCurve,
      // Per-region extra wall thickness (brushed onto the trim curve), aligned to trimCurve.
      trimThickness,
      showBandVoxelDebug: showBandVoxelDebug ?? false,
    });
  }

  // --- Trim-curve thickening phase -------------------------------------------------------

  private createTrimThickeningHost(): TrimThickeningHost {
    return {
      getRaycastTargets: () => {
        const jaw =
          this.activeTrimJaw === 'upper' ? this.jawsExport?.upperJaw : this.jawsExport?.lowerJaw;
        if (!jaw) return [];
        return [jaw as unknown as Object3D, ...(jaw.teeth as unknown as Object3D[])];
      },
      getTrimPoints: () => this.trimMarkerPoints,
      getBrushRadius: () => exportStore.getState().trimBrushRadius,
      getSelected: () => exportStore.getState().trimSelectedPoints,
      setBrush: (point, normal) => {
        if (!this.trimBrush) return;
        if (!point) {
          this.trimBrush.setVisible(false);
          return;
        }
        this.trimBrush.setRadius(exportStore.getState().trimBrushRadius);
        this.trimBrush.setPose(point, normal ?? new Vector3(0, 0, 1));
        this.trimBrush.setVisible(true);
      },
      onHover: (indices) => {
        this.trimHovered.clear();
        for (const i of indices) this.trimHovered.add(i);
        this.refreshTrimMarkers();
      },
      onPreview: (indices) => {
        this.trimPreview.clear();
        for (const i of indices) this.trimPreview.add(i);
        this.refreshTrimMarkers();
      },
      onCommit: (indices) => {
        this.trimPreview.clear();
        exportStore.getState().setTrimSelectedPoints(indices);
        this.refreshTrimMarkers();
      },
      setControlsEnabled: (enabled) => {
        this.root.controls.enabled = enabled;
      },
    };
  }

  private enterTrimThickening() {
    const jaw = this.activeTrimJaw;
    const stored = exportStore.getState().trimCurves[jaw];
    if (!stored?.length) {
      console.warn('[ExportScene] No confirmed trim curve to thicken for', jaw);
      exportStore.getState().setTrimThickeningActive(false);
      return;
    }
    exportStore.getState().setTrimThickeningJaw(jaw);
    this.trimMarkerPoints = stored.map((p) => new Vector3(p.x, p.y, p.z));
    this.buildTrimMarkers();
    if (!this.trimBrush) {
      this.trimBrush = new BrushVisualizer();
      this.trimThickeningGroup.add(this.trimBrush.group);
    }
    this.trimBrush.setVisible(false);
    toolManager.setActive(this.trimTool.name);
  }

  private exitTrimThickening() {
    if (toolManager.active === this.trimTool) toolManager.setActive(null);
    this.trimHovered.clear();
    this.trimPreview.clear();
    this.trimBrush?.setVisible(false);
    this.disposeTrimMarkers();
  }

  /** Build the pickable per-point marker cloud for the active jaw's trim curve. */
  private buildTrimMarkers() {
    this.disposeTrimMarkers();
    const count = this.trimMarkerPoints.length;
    if (count === 0) return;

    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const p = this.trimMarkerPoints[i];
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('aColor', new Float32BufferAttribute(new Float32Array(count * 3), 3));
    geometry.setAttribute('aSize', new Float32BufferAttribute(new Float32Array(count), 1));

    const material = new ShaderMaterial({
      vertexShader: TRIM_MARKER_VERTEX,
      fragmentShader: TRIM_MARKER_FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.trimMarkers = new Points(geometry, material);
    this.trimMarkers.name = 'TrimPointMarkers';
    this.trimMarkers.renderOrder = 250;
    this.trimThickeningGroup.add(this.trimMarkers);
    this.refreshTrimMarkers();
  }

  /** Recolor/resize markers from committed selection (store) + transient hover/preview + thickness. */
  private refreshTrimMarkers() {
    if (!this.trimMarkers) return;
    const geometry = this.trimMarkers.geometry;
    const colorAttr = geometry.getAttribute('aColor') as Float32BufferAttribute;
    const sizeAttr = geometry.getAttribute('aSize') as Float32BufferAttribute;
    const count = sizeAttr.count;

    const state = exportStore.getState();
    const selected = new Set(state.trimSelectedPoints);
    const thickness = state.trimThickness[this.activeTrimJaw];

    for (let i = 0; i < count; i++) {
      const isHovered = this.trimHovered.has(i);
      const isSelected = selected.has(i) || this.trimPreview.has(i);
      const color = isSelected
        ? TRIM_MARKER_SELECTED_COLOR
        : isHovered
          ? TRIM_MARKER_HOVER_COLOR
          : TRIM_MARKER_BASE_COLOR;
      colorAttr.setXYZ(i, color.r, color.g, color.b);

      let size = TRIM_MARKER_BASE_SIZE + (thickness?.[i] ?? 0) * TRIM_MARKER_SIZE_PER_MM;
      if (isHovered || isSelected) size += TRIM_MARKER_HIGHLIGHT_BUMP;
      sizeAttr.setX(i, size);
    }
    colorAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  }

  private disposeTrimMarkers() {
    if (!this.trimMarkers) return;
    this.trimThickeningGroup.remove(this.trimMarkers);
    this.trimMarkers.geometry.dispose();
    (this.trimMarkers.material as ShaderMaterial).dispose();
    this.trimMarkers = null;
  }

  private autoGenerateTrimCurve(jaw: TrimCurveJaw) {
    this.activeTrimJaw = jaw;
    if (!this.jawsExport) {
      exportStore.getState().setTrimCurveState('idle');
      return;
    }

    const jawExport = jaw === 'upper' ? this.jawsExport.upperJaw : this.jawsExport.lowerJaw;
    if (!jawExport) {
      console.warn(`[ExportScene] No ${jaw} jaw available for trimming curve.`);
      exportStore.getState().setTrimCurveState('idle');
      return;
    }

    const curve = this.resolveTrimCurvePoints(jawExport);
    if (!curve || curve.length < 3) {
      exportStore.getState().setTrimCurveState('idle');
      console.warn(`[ExportScene] Failed to generate trim curve for ${jaw} jaw.`);
      return;
    }

    const serialized: TrimCurvePoint[] = curve.map((p) => ({ x: p.x, y: p.y, z: p.z }));
    exportStore.getState().setTrimCurve(jaw, serialized);
    exportStore.getState().setTrimCurveState('ready');
    this.clearDebugTrim();
  }

  private resolveTrimCurvePoints(jawExport: JawExport): Vector3[] | null {
    // Sample the smooth spline densely for the cut so a small, easy-to-drag control-point
    // set still yields a high-fidelity (non-faceted) trim boundary.
    const interactiveSpline = 'trimCurve' in jawExport ? jawExport.trimCurve : null;
    const threeCurve = interactiveSpline?.getCurve() ?? null;
    if (threeCurve) {
      // ~0.3 mm arc-length spacing: the trim height field adopts the height of the NEAREST
      // sampled point, so sparse or uneven samples (uniform-t bunches near control points,
      // leaving 1.5 mm+ gaps elsewhere) step the cut height at interproximal dips.
      const spacingMm = 0.3;
      const samples = Math.min(4000, Math.max(120, Math.ceil(threeCurve.getLength() / spacingMm)));
      const sampled = threeCurve.getSpacedPoints(samples);
      // Closed curves repeat the first point at the end — drop the duplicate.
      if (sampled.length > 1 && sampled[0].distanceTo(sampled[sampled.length - 1]) < 1e-6) {
        sampled.pop();
      }
      if (sampled.length >= 3) {
        return sampled;
      }
    }

    const trimPoints = jawExport.getTrimCurvePoints();
    return trimPoints.length >= 3 ? trimPoints : null;
  }

  private ensurePositive(value: number, fallback: number, min: number) {
    if (Number.isFinite(value) && value > min) {
      return value;
    }
    return fallback;
  }

  private clearAligners() {
    this.alignerGroup.children.forEach((child) => {
      if (child instanceof Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((mat) => mat.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
    this.alignerGroup.clear();
    this.clearDebugTrim();
    this.clearBandVoxelDebug();
  }

  private clearBandVoxelDebug() {
    this.bandVoxelDebugGroup.children.forEach((child) => {
      if (child instanceof Points) {
        child.geometry.dispose();
        (child.material as PointsMaterial).dispose();
      } else if (child instanceof Mesh) {
        child.geometry.dispose();
        (child.material as MeshBasicMaterial).dispose();
      }
    });
    this.bandVoxelDebugGroup.clear();
  }

  /** Translucent wireframe of the V7 gingival trim cut-solid (dilated/dropped trim loop). */
  private showTrimCutDebug(geometry: BufferGeometry) {
    const material = new MeshBasicMaterial({
      color: 0xff8800,
      wireframe: true,
      transparent: true,
      opacity: 0.5,
      side: DoubleSide,
      depthTest: false,
    });
    const mesh = new Mesh(geometry, material);
    mesh.name = 'TrimCutSolid';
    mesh.renderOrder = 213;
    this.bandVoxelDebugGroup.add(mesh);
    this.bandVoxelDebugGroup.visible = exportStore.getState().bandVoxelDebugVisible;
  }

  private showBandVoxelDebug(layers: BandVoxelDebugLayers, voxelSizeMm: number) {
    this.clearBandVoxelDebug();
    const pointSize = Math.max(voxelSizeMm * 0.9, 0.12);

    const addLayer = (
      geometry: BufferGeometry | undefined,
      color: number,
      name: string,
      renderOrder: number,
    ) => {
      if (!geometry || geometry.getAttribute('position').count === 0) return;
      const hasColors = geometry.getAttribute('color') !== undefined;
      const material = new PointsMaterial({
        color: hasColors ? 0xffffff : color,
        vertexColors: hasColors,
        size: pointSize,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
      });
      const points = new Points(geometry, material);
      points.name = name;
      points.renderOrder = renderOrder;
      this.bandVoxelDebugGroup.add(points);
    };

    addLayer(layers.band, 0x22d3ee, 'BandVoxels', 210);
    addLayer(layers.barrier, 0xff4488, 'BarrierVoxels', 211);
    addLayer(layers.flooded, 0xfbbf24, 'FloodedVoxels', 212);
    this.bandVoxelDebugGroup.visible = exportStore.getState().bandVoxelDebugVisible;
  }

  private clearDebugTrim() {
    this.debugTrimGroup.children.forEach((child) => {
      if (child instanceof LineLoop) {
        child.geometry.dispose();
        (child.material as LineBasicMaterial).dispose();
      }
    });
    this.debugTrimGroup.clear();
  }

  public override dispose() {
    this.clearAligners();
    this.exitTrimThickening();
    toolManager.unregister(this.trimTool.name);
    this.trimBrush?.dispose();
    this.trimBrush = null;
    this.trimThickeningGroup.removeFromParent();
    this.alignerGroup.removeFromParent();
    this.debugTrimGroup.removeFromParent();
    this.bandVoxelDebugGroup.removeFromParent();
    this.exportService.disposeJaws(this.jawsExport);
    this.exportService.dispose();
    if (this.unsubscribeCappingDebug) {
      this.unsubscribeCappingDebug();
      this.unsubscribeCappingDebug = null;
    }
    if (this.unsubscribeBandVoxelDebug) {
      this.unsubscribeBandVoxelDebug();
      this.unsubscribeBandVoxelDebug = null;
    }
    if (this.unsubscribeTrimThickening) {
      this.unsubscribeTrimThickening();
      this.unsubscribeTrimThickening = null;
    }
    super.dispose();
  }
}
